import * as React from "react";
import { Alert, Box, Button, Card, Chip, Divider, Link, Stack, Tooltip, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { useParams } from "react-router-dom";

import { useCancelRun, usePublishRun, useRun } from "@/hooks/api/use-proving-ground";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { RouterLink } from "@/components/core/link";

import { MONO, standingOf, verdictInk } from "../shared";
import { CellDrawer } from "./cell-drawer";
import { RunMatrix } from "./matrix";
import { Suggestions } from "./suggestions";

const LABEL_SX = {
	fontFamily: MONO,
	fontSize: 9,
	fontWeight: 500,
	letterSpacing: ".07em",
	textTransform: "uppercase" as const,
	color: "text.secondary",
	display: "block",
};

/** Figures set in monospace and aligned, so a column of them can be compared by eye. */
const FIGURE_SX = {
	fontFamily: MONO,
	fontSize: 15,
	fontWeight: 600,
	fontVariantNumeric: "tabular-nums" as const,
	lineHeight: 1.4,
};

function Stat({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
	return (
		<Box sx={{ px: 2, py: 1.25, borderRight: 1, borderColor: "divider", flex: "1 1 auto", minWidth: 118 }}>
			<Typography sx={LABEL_SX}>{label}</Typography>
			<Box sx={{ mt: 0.4 }}>{children}</Box>
		</Box>
	);
}

export function ProvingGroundRunView(): React.JSX.Element {
	const { runId } = useParams();
	const id = Number(runId);

	const { data: run, isLoading } = useRun(Number.isFinite(id) ? id : undefined);
	const publish = usePublishRun();

	/**
	 * What this reader may do, as stated by the server.
	 *
	 * Previously inferred from whether the prompt came back, which conflated reading with
	 * acting: an admin is shown the prompt and cannot publish, so the inference offered them a
	 * button that 403s. The gate is the only thing that knows, so it is the thing that answers.
	 */
	const canPublish = Boolean(run?.canPublish);
	const canEdit = Boolean(run?.canEdit);
	const cancel = useCancelRun();

	const [openCell, setOpenCell] = React.useState<number | null>(null);

	const inFlight = run?.status === "queued" || run?.status === "running";

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction="column" spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href="/proving-ground/runs"
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Runs
							</Link>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary">
								Run #{runId}
							</Typography>
						</Breadcrumbs>

						<Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
							<Box sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">{run?.agentName ?? "Run"}</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									{run?.scenarios.length ?? 0} scenarios × {run?.personas.length ?? 0} personas
								</Typography>
							</Box>
							{run?.publishedAt ? (
								<Chip
									size="small"
									color="success"
									variant="outlined"
									label={`Published ${new Date(run.publishedAt).toLocaleDateString()}`}
								/>
							) : null}
							{inFlight ? (
								<Button variant="outlined" color="error" onClick={() => cancel.mutate(id)} disabled={cancel.isPending}>
									Cancel run
								</Button>
							) : null}
							{/* Publishing is what puts a run in front of managers, so it is only offered on a
							    finished one and only to whoever can already see the prompt behind it. */}
							{canPublish && run?.status === "completed" ? (
								<Button
									variant={run.publishedAt ? "outlined" : "contained"}
									color={run.publishedAt ? "inherit" : "primary"}
									disabled={publish.isPending}
									onClick={() => publish.mutate({ id, published: !run.publishedAt })}
								>
									{run.publishedAt ? "Unpublish" : "Publish to team"}
								</Button>
							) : null}
						</Stack>
					</Stack>

					{run?.error ? <Alert severity="error">{run.error}</Alert> : null}

					{run?.mode === "text" ? (
						<Alert severity="info">
							This was a text run. It tests what the agent says, not how it speaks or listens — interruption,
							mispronunciation and mishearing are invisible here. Re-run in voice mode before trusting it to certify
							anything.
						</Alert>
					) : null}

					<Card sx={{ position: "relative" }}>
						<ComponentLoader loading={isLoading} />

						<Stack direction="row" sx={{ flexWrap: "wrap", borderBottom: 1, borderColor: "divider" }}>
							<Stat label="Pass rate">
								<Typography
									sx={[
										FIGURE_SX,
										verdictInk(standingOf(run?.passedChecks ?? 0, (run?.totalChecks ?? 0) - (run?.passedChecks ?? 0))),
									]}
								>
									{run?.totalChecks ? `${Math.round((run.passedChecks / run.totalChecks) * 100)}%` : "—"}
								</Typography>
							</Stat>
							<Stat label="Checks">
								<Typography sx={FIGURE_SX}>
									{run?.passedChecks ?? 0}
									<Box component="span" sx={{ color: "text.secondary" }}>
										/{run?.totalChecks ?? 0}
									</Box>
								</Typography>
							</Stat>
							<Stat label="Calls">
								<Typography sx={FIGURE_SX}>
									{run?.completedCells ?? 0}
									<Box component="span" sx={{ color: "text.secondary" }}>
										/{run?.totalCells ?? 0}
									</Box>
								</Typography>
							</Stat>
							<Stat label="Mode">
								<Typography sx={[FIGURE_SX, { fontSize: 13, textTransform: "uppercase", letterSpacing: ".04em" }]}>
									{run?.mode ?? "—"}
								</Typography>
							</Stat>
							<Stat label="Status">
								<Typography
									sx={[
										FIGURE_SX,
										{ fontSize: 13, textTransform: "uppercase", letterSpacing: ".04em" },
										run?.status === "failed"
											? verdictInk("gate")
											: { color: run?.status === "completed" ? "text.primary" : "text.secondary" },
									]}
								>
									{run?.status ?? "—"}
								</Typography>
							</Stat>
							<Stat label="Duration">
								<Typography sx={FIGURE_SX}>
									{run?.durationMs ? `${Math.round(run.durationMs / 1000)}s` : "—"}
								</Typography>
							</Stat>
							{/* Hidden outright when the reader is not shown cost. Absent is not zero, and
							    the previous fallback printed "0.0k" beside an instruction to set rates on a
							    page that reader cannot open. */}
							{run && run.inputTokens === undefined ? null : (
								<Stat label="Spend">
									{/* Unpriced is not the same as free, and a partial figure is not the same as a
								    whole one — so the tooltip always names what the number does and does not cover. */}
									<Tooltip
										arrow
										title={
											run?.spendUsd == null
												? "Set rates in Proving Ground settings — models per million tokens, speech per million characters, transcription per minute."
												: `Covers ${(run.spendCovers ?? []).join(", ")}. ${(
														(run.inputTokens ?? 0) + (run.outputTokens ?? 0)
													).toLocaleString()} tokens · ${(run.ttsCharacters ?? 0).toLocaleString()} characters synthesised · ${Math.round(
														(run.sttSeconds ?? 0) / 60
													)} transcription minutes.`
										}
									>
										<Typography sx={FIGURE_SX}>
											{run?.spendUsd == null
												? `${(((run?.inputTokens ?? 0) + (run?.outputTokens ?? 0)) / 1000).toFixed(1)}k`
												: `$${run.spendUsd.toFixed(4)}`}
										</Typography>
									</Tooltip>
									{run?.spendCovers?.length && run.spendCovers.length < 3 ? (
										<Typography sx={[LABEL_SX, verdictInk("review"), { mt: 0.25 }]}>
											{run.spendCovers.join(" + ")} only
										</Typography>
									) : null}
								</Stat>
							)}
						</Stack>

						<RunMatrix run={run} onOpenCell={setOpenCell} />

						<Divider />
						<Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2 }}>
							One call per row, required behaviours across. A column that fails everywhere is a prompt problem; a row
							that fails everywhere is a call the agent cannot handle. Narrow to one scenario to compare callers on it
							directly. Click any cell to read the transcript and the judge&rsquo;s reasoning — and to disagree with it.
						</Typography>
					</Card>

					{/* After the grid, not before it: the numbers are the evidence and this is the
					    argument drawn from them, so it reads in that order. Hidden entirely from a
					    manager, whose copy of the run carries neither the prompt nor the advice. */}
					{canPublish ? (
						<Box>
							<Typography variant="h6" sx={{ mb: 0.5 }}>
								What to change
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Read from this run&rsquo;s failures against {run?.agentName ?? "the agent"}&rsquo;s prompt.
							</Typography>
							<Suggestions
								suggestions={run?.suggestions ?? []}
								suggestionsAt={run?.suggestionsAt ?? null}
								status={run?.status ?? ""}
								failedChecks={(run?.totalChecks ?? 0) - (run?.passedChecks ?? 0)}
								totalChecks={run?.totalChecks ?? 0}
								runId={id}
								canApply={canEdit}
							/>
						</Box>
					) : null}
				</Stack>
			</Box>

			<CellDrawer cellId={openCell} onClose={() => setOpenCell(null)} />
		</React.Fragment>
	);
}
